import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { Reveal, Accent } from "@/components/Reveal";
import { getPages, SITE_URL } from "@/lib/servicePages";
import { useLenis } from "@/lib/useLenis";
import { useSeo } from "@/lib/useSeo";
import { useLang } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

export default function ServicesIndex() {
  useLenis();
  const { t, lang } = useLang();
  const ts = t.service;
  const pages = getPages(lang);
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: ts.indexSeoTitle,
      itemListElement: pages.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.title, url: `${SITE_URL}/uslugi/${s.slug}` })),
    }),
    [pages, ts.indexSeoTitle]
  );
  useSeo({ title: ts.indexSeoTitle, description: ts.indexSeoDesc, canonical: `${SITE_URL}/uslugi`, jsonLd });

  return (
    <div data-testid="services-index-page" className="bg-ink text-mist font-sans antialiased">
      <Nav />
      <main>
        <section className="pt-32 md:pt-44 pb-16 md:pb-24 max-w-7xl mx-auto px-6 md:px-10">
          <motion.p initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }} className="font-mono text-xs uppercase tracking-[0.4em] text-gold mb-8 flex items-center gap-4">
            <span className="inline-block w-10 h-px bg-gold" aria-hidden="true" />
            {ts.indexKicker}
          </motion.p>
          <h1 data-testid="services-index-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-mist max-w-3xl overflow-hidden">
            <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.15, ease: EASE }} className="block">
              <Accent parts={ts.indexTitle} />
            </motion.span>
          </h1>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-36">
          <div className="flex flex-col divide-y divide-line border-y border-line">
            {pages.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05}>
                <Link
                  to={`/uslugi/${s.slug}`}
                  data-testid={`services-index-link-${s.id}`}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline py-8 md:py-10 hover:bg-coal/40 transition-colors duration-500 md:px-4 md:-mx-4"
                >
                  <span className="md:col-span-1 font-mono text-sm text-gold">{s.num}</span>
                  <h2 className="md:col-span-5 font-serif text-2xl md:text-3xl lg:text-4xl text-mist leading-tight group-hover:text-gold transition-colors duration-300">
                    {s.title}
                  </h2>
                  <p className="md:col-span-5 text-dim text-sm md:text-base leading-relaxed">{s.intro}</p>
                  <span className="md:col-span-1 md:justify-self-end text-dim group-hover:text-gold transition-[color,transform] duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">
                    <ArrowUpRight size={22} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
